import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { motion, useScroll, useSpring, useTransform, useMotionValue, useInView, type MotionStyle, type Variants } from 'framer-motion';
import { containerStagger, fadeUpItem } from './depth-motion';

/* ──────────────────────────────────────────────────────────────────────────
 * Aurora — animated gradient mesh background. Drop into any section that
 * needs ambient depth. Three softly drifting blobs at staggered timings.
 * ────────────────────────────────────────────────────────────────────────── */
type AuroraProps = {
  className?: string;
  /** 'indigo' (default), 'gold', or 'mixed' */
  palette?: 'indigo' | 'gold' | 'mixed' | 'cool';
  intensity?: number;
};

export function Aurora({ className = '', palette = 'mixed', intensity = 1 }: AuroraProps) {
  const palettes = {
    indigo: { a: 'rgba(108,92,231,0.18)', b: 'rgba(74,52,199,0.14)', c: 'rgba(165,148,249,0.12)' },
    gold: { a: 'rgba(240,165,0,0.16)', b: 'rgba(245,182,39,0.12)', c: 'rgba(255,209,102,0.1)' },
    mixed: { a: 'rgba(108,92,231,0.16)', b: 'rgba(240,165,0,0.13)', c: 'rgba(165,148,249,0.1)' },
    cool: { a: 'rgba(116,185,255,0.14)', b: 'rgba(108,92,231,0.13)', c: 'rgba(85,239,196,0.08)' },
  } as const;
  const p = palettes[palette];
  const k = intensity;
  return (
    <div aria-hidden className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      <div
        className="aurora-blob-a absolute -top-[20%] -left-[10%] w-[60vw] h-[60vw] rounded-full blur-[120px]"
        style={{ background: p.a, opacity: 0.9 * k }}
      />
      <div
        className="aurora-blob-b absolute top-[30%] -right-[15%] w-[55vw] h-[55vw] rounded-full blur-[140px]"
        style={{ background: p.b, opacity: 0.8 * k }}
      />
      <div
        className="aurora-blob-c absolute -bottom-[20%] left-[20%] w-[50vw] h-[50vw] rounded-full blur-[140px]"
        style={{ background: p.c, opacity: 0.7 * k }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * ScrollVeil — site-wide ambient backdrop. A fixed-position aurora that
 * lives behind every section and parallaxes with overall page scroll.
 * ────────────────────────────────────────────────────────────────────────── */
export function ScrollVeil() {
  const { scrollYProgress } = useScroll();
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -340]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -520]);
  return (
    <div aria-hidden className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
      <motion.div
        style={{ y: y1, background: 'radial-gradient(circle at 20% 30%, rgba(108,92,231,0.14), transparent 60%)' }}
        className="absolute inset-0"
      />
      <motion.div
        style={{ y: y2, background: 'radial-gradient(circle at 80% 20%, rgba(240,165,0,0.10), transparent 55%)' }}
        className="absolute inset-0"
      />
      <motion.div
        style={{ y: y3, background: 'radial-gradient(circle at 60% 90%, rgba(165,148,249,0.10), transparent 60%)' }}
        className="absolute inset-0"
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * FloatingParticles — neural-network nodes that drift across the viewport.
 * Designed to live in PublicLayout for global continuity (pointer-events: none).
 * ────────────────────────────────────────────────────────────────────────── */
export function FloatingParticles({ count = 14, opacity = 0.18 }: { count?: number; opacity?: number }) {
  const particles = Array.from({ length: count }).map((_, i) => ({
    x: ((i * 37) % 100),
    y: ((i * 53) % 100),
    size: 2 + (i % 4),
    duration: 4 + (i % 5),
    delay: (i * 0.5) % 3,
  }));
  return (
    <div aria-hidden className="fixed inset-0 -z-[5] overflow-hidden pointer-events-none">
      {particles.map((p, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-indigo-400"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, opacity }}
          animate={{ opacity: [opacity, opacity * 2.2, opacity], scale: [1, 1.4, 1] }}
          transition={{ duration: p.duration, repeat: Infinity, ease: 'easeInOut', delay: p.delay }}
        />
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * CursorGlow — radial gradient that follows the mouse. Hidden on touch.
 * ────────────────────────────────────────────────────────────────────────── */
export function CursorGlow() {
  const x = useMotionValue(-1000);
  const y = useMotionValue(-1000);
  const sx = useSpring(x, { stiffness: 150, damping: 20, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 150, damping: 20, mass: 0.4 });

  useEffect(() => {
    if (window.matchMedia('(hover: none)').matches) return;
    const onMove = (e: MouseEvent) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [x, y]);

  return (
    <motion.div
      aria-hidden
      className="fixed pointer-events-none -z-[4] hidden md:block"
      style={{
        x: sx,
        y: sy,
        width: 480,
        height: 480,
        marginLeft: -240,
        marginTop: -240,
        background: 'radial-gradient(circle, rgba(108,92,231,0.10) 0%, rgba(108,92,231,0.04) 30%, transparent 65%)',
        borderRadius: '50%',
      }}
    />
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * MagneticCard — subtle 3D tilt + glow that follows the cursor.
 * ────────────────────────────────────────────────────────────────────────── */
type MagneticCardProps = {
  children: ReactNode;
  className?: string;
  intensity?: number;
  glow?: 'indigo' | 'gold' | 'mixed';
};

export function MagneticCard({ children, className = '', intensity = 8, glow = 'indigo' }: MagneticCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotX = useSpring(useTransform(y, [-1, 1], [intensity, -intensity]), { stiffness: 200, damping: 18 });
  const rotY = useSpring(useTransform(x, [-1, 1], [-intensity, intensity]), { stiffness: 200, damping: 18 });
  const glowColor = glow === 'gold'
    ? 'rgba(240,165,0,0.18)'
    : glow === 'mixed'
      ? 'rgba(165,148,249,0.18)'
      : 'rgba(108,92,231,0.22)';

  function onMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    x.set(((e.clientX - r.left) / r.width - 0.5) * 2);
    y.set(((e.clientY - r.top) / r.height - 0.5) * 2);
  }
  function onMouseLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{ rotateX: rotX, rotateY: rotY, transformStyle: 'preserve-3d' }}
      whileHover={{ y: -4, boxShadow: `0 20px 50px -20px ${glowColor}` }}
      transition={{ type: 'spring', stiffness: 200, damping: 22 }}
      className={`will-change-transform ${className}`}
    >
      {children}
    </motion.div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * GlowBorder — wraps content in an animated conic-gradient border.
 * Use the .gradient-border CSS class directly when nesting in a card already.
 * ────────────────────────────────────────────────────────────────────────── */
export function GlowBorder({
  children,
  className = '',
  rounded = 'rounded-lg',
  animated = true,
  style,
}: { children: ReactNode; className?: string; rounded?: string; animated?: boolean; style?: CSSProperties }) {
  return (
    <div
      className={`gradient-border ${animated ? '' : 'gradient-border-static'} ${rounded} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * SectionDivider — a thin gradient hairline plus optional floating motif.
 * ────────────────────────────────────────────────────────────────────────── */
export function SectionDivider({ withDot = true }: { withDot?: boolean }) {
  return (
    // -mt-3 pulls the divider up by half its own height (h-6 = 24px) so the
    // hairline at top-1/2 lands flush with the previous section's bottom edge.
    // -mb-2 keeps the next section pulled in tightly underneath.
    <div className="relative h-6 -mt-3 -mb-2">
      <div className="section-divider absolute inset-x-0 top-1/2" />
      {withDot ? (
        <motion.div
          aria-hidden
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gold-500"
          animate={{ scale: [1, 1.4, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />
      ) : null}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * AnimatedNumber — counts up to `value` once visible.
 * ────────────────────────────────────────────────────────────────────────── */
// Module-scope so the default has a stable identity across renders. Default
// parameters create a fresh function each render — that would put `format` in
// the useEffect deps with a new reference every time, restarting the count-up
// in an infinite loop.
const defaultNumberFormat = (n: number) => Math.round(n).toLocaleString();

export function AnimatedNumber({ value, format = defaultNumberFormat, className = '', duration = 1.6 }: {
  value: number; format?: (n: number) => string; className?: string; duration?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState('0');

  useEffect(() => {
    if (!inView) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(format(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration, format]);

  return <span ref={ref} className={className}>{display}</span>;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Reveal helpers — opinionated whileInView wrappers with stagger.
 * Variants live in ./depth-motion so this component module stays HMR-clean.
 * ────────────────────────────────────────────────────────────────────────── */
export function Reveal({
  children,
  variants = fadeUpItem,
  className = '',
  amount = 0.3,
  delay,
  style,
}: {
  children: ReactNode;
  variants?: Variants;
  className?: string;
  amount?: number;
  delay?: number;
  style?: MotionStyle;
}) {
  return (
    <motion.div
      className={className}
      style={style}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount }}
      variants={variants}
      transition={delay !== undefined ? { delay } : undefined}
    >
      {children}
    </motion.div>
  );
}

export function StaggerGroup({
  children,
  className = '',
  amount = 0.2,
  style,
}: {
  children: ReactNode;
  className?: string;
  amount?: number;
  style?: CSSProperties;
}) {
  return (
    <motion.div
      className={className}
      style={style}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount }}
      variants={containerStagger}
    >
      {children}
    </motion.div>
  );
}

